/**
 * Chapitre 2 — « La chaîne d'information : l'éclairage public ».
 *
 * L'îlot construit une chaîne en quatre cases (acquérir, traiter, communiquer,
 * agir), la fait évaluer, puis la déploie si le budget le permet.
 */
import { CHAIN_ROLES } from './content-schema.js';
import type {
  ChainRole,
  Chapter,
  Content,
  Effects,
  Equipment,
  Solution,
} from './content-schema.js';
import { playerMessage } from './content-messages.js';
import { applyEffects, sumEffects } from './gauges.js';
import type { ActionRefusal, ActionResult, ChainState, GameState } from './state.js';
import { EMPTY_CHAIN, currentChapter, refus, withAction } from './state.js';

/**
 * Nombre de retours à l'étape « Concevoir » autorisés après un déploiement.
 *
 * Le plan en accorde un : de quoi corriger le tir après un événement, sans
 * transformer la séance en essais-erreurs sans fin.
 */

/* ------------------------------------------------------------------ */
/* Capacité des cases                                                  */
/* ------------------------------------------------------------------ */

/**
 * Combien d'équipements la case accepte-t-elle, compte tenu de l'état actuel
 * de la chaîne ?
 *
 * Cas particulier documenté par `solutions[].note_moteur` sur
 * `optimale_supervisee` : la case « traiter » accepte une carte programmable
 * **et** un serveur de supervision, mais seulement si la case « communiquer »
 * est remplie — sans réseau, un serveur ne recevrait rien.
 */
export function slotCapacity(chapitre: Chapter, chain: ChainState, slot: ChainRole): number {
  const definition = chapitre.cases_chaine?.find((c) => c.id === slot);
  const max = definition?.max ?? 1;

  if (slot === 'traiter' && chain.communiquer.length > 0) {
    return Math.max(max, 2);
  }
  return max;
}

/**
 * Ce que la case accepterait si la chaîne était reliée à un réseau.
 *
 * Sert à distinguer une case vraiment pleine d'une case qu'un module de
 * communication débloquerait. On interroge `slotCapacity` avec une chaîne
 * fictive plutôt que de redire sa règle ici : une seule définition, donc pas
 * de dérive entre le refus et la capacité réelle.
 */
function capaciteAvecReseau(chapitre: Chapter, chain: ChainState, slot: ChainRole): number {
  return slotCapacity(chapitre, { ...chain, communiquer: ['reseau_fictif'] }, slot);
}

const equipmentsById = (content: Content): ReadonlyMap<string, Equipment> =>
  new Map(content.equipements.map((e) => [e.id, e]));

/** Tous les équipements posés dans la chaîne, toutes cases confondues. */
export function chainEquipmentIds(chain: ChainState): readonly string[] {
  return CHAIN_ROLES.flatMap((role) => chain[role]);
}

/* ------------------------------------------------------------------ */
/* Étape 2 — Concevoir : poser et retirer des équipements              */
/* ------------------------------------------------------------------ */

/** Pose un équipement dans une case, en respectant son rôle et la capacité de la case. */
export function placeEquipment(
  state: GameState,
  content: Content,
  slot: ChainRole,
  equipmentId: string,
): ActionResult {
  const equipement = equipmentsById(content).get(equipmentId);
  if (!equipement) {
    return refus('equipement_inconnu', `Équipement absent du contenu : « ${equipmentId} ».`);
  }
  if (equipement.role !== slot) {
    return refus('role_incompatible', playerMessage(content, 'role_incompatible'));
  }
  if (chainEquipmentIds(state.chain).includes(equipmentId)) {
    return refus('equipement_deja_place', playerMessage(content, 'equipement_deja_place'));
  }

  const chapitre = currentChapter(state, content);
  const capacite = slotCapacity(chapitre, state.chain, slot);
  if (state.chain[slot].length >= capacite) {
    // Sans ce cas, « Traiter » se contentait d'annoncer qu'elle était pleine.
    // L'îlot qui voulait ajouter un serveur de supervision n'avait alors aucun
    // moyen de savoir qu'un module de communication l'aurait débloquée : la
    // solution trois étoiles la plus complète n'était atteignable que dans un
    // ordre de pose que rien n'énonçait.
    if (capaciteAvecReseau(chapitre, state.chain, slot) > state.chain[slot].length) {
      return refus(
        'reseau_requis_pour_superviser',
        playerMessage(content, 'reseau_requis_pour_superviser'),
      );
    }
    return refus('case_pleine', playerMessage(content, 'case_pleine'));
  }

  const chain: ChainState = { ...state.chain, [slot]: [...state.chain[slot], equipmentId] };

  return {
    ok: true,
    state: withAction({ ...state, chain }, { type: 'placer_equipement', slot, equipmentId }),
  };
}

/** Retire un équipement d'une case. */
export function removeEquipment(
  state: GameState,
  content: Content,
  slot: ChainRole,
  equipmentId: string,
): ActionResult {
  if (!state.chain[slot].includes(equipmentId)) {
    return refus(
      'equipement_absent',
      `L'équipement « ${equipmentId} » n'est pas posé dans la case « ${slot} ».`,
    );
  }

  const chain: ChainState = {
    ...state.chain,
    [slot]: state.chain[slot].filter((id) => id !== equipmentId),
  };

  // Retirer le moyen de communication ramène la case « traiter » à un seul
  // élément : on refuse plutôt que de laisser une chaîne impossible à évaluer.
  const chapitre = currentChapter(state, content);
  if (chain.traiter.length > slotCapacity(chapitre, chain, 'traiter')) {
    return refus(
      'retrait_impossible',
      `Retirer « ${equipmentId} » laisserait ${chain.traiter.length} équipements dans la case « traiter ». Retirer d'abord le serveur de supervision.`,
    );
  }

  return {
    ok: true,
    state: withAction({ ...state, chain }, { type: 'retirer_equipement', slot, equipmentId }),
  };
}

/** Vide la chaîne, pour repartir de zéro à l'étape « Améliorer ». */
export function clearChain(state: GameState): GameState {
  return withAction({ ...state, chain: EMPTY_CHAIN }, { type: 'vider_chaine' });
}

/* ------------------------------------------------------------------ */
/* Étape 3 — Tester : évaluation de la chaîne                          */
/* ------------------------------------------------------------------ */

export type ChainStatus = 'solution' | 'inadapte' | 'incomplet' | 'inconnue';

export interface ChainEvaluation {
  readonly status: ChainStatus;
  /** La solution reconnue, quand `status` vaut « solution ». */
  readonly solution: Solution | null;
  /** La solution listée la plus proche, quand `status` vaut « inconnue ». */
  readonly solutionProche: Solution | null;
  /** Ce qu'il faut afficher à l'îlot, dans l'ordre. */
  readonly messages: readonly string[];
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

/** La chaîne correspond-elle exactement à cette combinaison, ordre mis à part ? */
function matchesCombination(chain: ChainState, solution: Solution): boolean {
  return CHAIN_ROLES.every((role) => sameSet(chain[role], solution.combinaison[role]));
}

/** Nombre d'éléments qui diffèrent entre la chaîne et une combinaison listée. */
export function chainDistance(chain: ChainState, solution: Solution): number {
  return CHAIN_ROLES.reduce((distance, role) => {
    const posee = chain[role];
    const attendue = solution.combinaison[role];
    const enTrop = posee.filter((id) => !attendue.includes(id)).length;
    const manquants = attendue.filter((id) => !posee.includes(id)).length;
    return distance + enTrop + manquants;
  }, 0);
}

/**
 * Évalue la chaîne construite par l'îlot pour un problème donné, selon
 * `regles_moteur.combinaison_inconnue` :
 *
 * 1. correspondance exacte avec une solution listée → « solution » ;
 * 2. sinon, un équipement hors des `usages` du problème → « inadapte », avec
 *    son `message_inadapte` ;
 * 3. sinon, une case obligatoire vide → « incomplet » ;
 * 4. sinon → « inconnue », avec le `feedback` de la solution la plus proche.
 */
export function evaluateChain(
  state: GameState,
  content: Content,
  problemId: string,
): ChainEvaluation {
  const probleme = content.problemes.find((p) => p.id === problemId);
  if (!probleme) {
    throw new Error(`Problème absent du contenu : « ${problemId} ».`);
  }

  const solutions = content.solutions.filter((s) => s.probleme === problemId);
  const index = equipmentsById(content);
  const poses = chainEquipmentIds(state.chain);

  const exacte = solutions.find((solution) => matchesCombination(state.chain, solution));
  if (exacte) {
    return {
      status: 'solution',
      solution: exacte,
      solutionProche: null,
      messages: [exacte.feedback],
    };
  }

  const messagesInadaptes = poses
    .map((id) => index.get(id))
    .filter((equipement): equipement is Equipment => equipement !== undefined)
    .filter((equipement) => !equipement.usages.includes(problemId))
    .map((equipement) => equipement.message_inadapte)
    .filter((message): message is string => message !== null);

  if (messagesInadaptes.length > 0) {
    return {
      status: 'inadapte',
      solution: null,
      solutionProche: null,
      messages: messagesInadaptes,
    };
  }

  const chapitre = currentChapter(state, content);
  const casesManquantes = (chapitre.cases_chaine ?? []).filter(
    (definition) => definition.obligatoire && state.chain[definition.id].length === 0,
  );

  if (casesManquantes.length > 0) {
    return {
      status: 'incomplet',
      solution: null,
      solutionProche: null,
      messages: casesManquantes.map((definition) => definition.question),
    };
  }

  const proche = [...solutions].sort(
    (a, b) => chainDistance(state.chain, a) - chainDistance(state.chain, b),
  )[0];

  return {
    status: 'inconnue',
    solution: null,
    solutionProche: proche ?? null,
    messages: proche ? [proche.feedback] : [],
  };
}

/* ------------------------------------------------------------------ */
/* Coût et effets                                                      */
/* ------------------------------------------------------------------ */

/** Coût d'installation de la chaîne : la somme des coûts des équipements posés. */
export function computeDeploymentCost(state: GameState, content: Content): number {
  const index = equipmentsById(content);
  return chainEquipmentIds(state.chain).reduce(
    (total, id) => total + (index.get(id)?.cout ?? 0),
    0,
  );
}

/**
 * Effets totaux du déploiement, selon `regles_moteur.calcul_effets` :
 * effets d'usage de la solution + impacts de fabrication + effets permanents
 * des équipements posés. Le coût n'est pas compté ici : `deploy` le retire du
 * budget séparément.
 */
export function computeTotalEffects(
  state: GameState,
  content: Content,
  solution: Solution,
): Effects {
  const index = equipmentsById(content);
  const equipements = chainEquipmentIds(state.chain)
    .map((id) => index.get(id))
    .filter((equipement): equipement is Equipment => equipement !== undefined);

  return sumEffects(
    solution.effets_usage,
    ...equipements.map((equipement) => equipement.impact_fabrication),
    ...equipements.map((equipement) => equipement.effet_permanent ?? {}),
  );
}

/* ------------------------------------------------------------------ */
/* Déploiement                                                         */
/* ------------------------------------------------------------------ */

export interface DeploymentReport {
  readonly solution: Solution;
  readonly cout: number;
  readonly effets: Effects;
  readonly etoiles: number;
}

export type DeployResult =
  | { readonly ok: true; readonly state: GameState; readonly rapport: DeploymentReport }
  | ActionRefusal;

/**
 * Déploie la chaîne dans la ville.
 *
 * Refuse si la chaîne ne correspond à aucune solution listée, ou si le budget
 * ne suffit pas — dans ce cas avec le message de `regles_moteur.budget_insuffisant`,
 * le seul message de refus destiné à l'élève.
 */
export function deploy(state: GameState, content: Content, problemId: string): DeployResult {
  const evaluation = evaluateChain(state, content, problemId);
  if (evaluation.status !== 'solution' || !evaluation.solution) {
    return refus(
      'chaine_non_deployable',
      `La chaîne ne correspond à aucune solution connue (état « ${evaluation.status} »).`,
    );
  }

  const solution = evaluation.solution;
  const cout = computeDeploymentCost(state, content);

  if (cout > state.gauges.budget) {
    return refus('budget_insuffisant', playerMessage(content, 'budget_insuffisant'));
  }

  const effets = computeTotalEffects(state, content, solution);
  const avecEffets = applyEffects(state, { ...effets, budget: -cout }, content);

  return {
    ok: true,
    state: withAction(
      {
        ...avecEffets,
        deployedSolutionId: solution.id,
        // Photo des jauges d'avant : elle sert au bilan de la fiche technique
        // et au retour à l'étape Concevoir.
        gaugesBeforeDeploy: state.gauges,
      },
      { type: 'deployer', solutionId: solution.id, cout },
    ),
    rapport: { solution, cout, effets, etoiles: solution.etoiles },
  };
}

/* ------------------------------------------------------------------ */
/* Étape 1 — Comprendre : les questions guidées                        */
/* ------------------------------------------------------------------ */

/** Enregistre la réponse de l'îlot à une question guidée du chapitre. */
export function answerGuidedQuestion(
  state: GameState,
  content: Content,
  questionId: string,
  choiceId: string,
): ActionResult {
  const chapitre = currentChapter(state, content);
  const question = chapitre.questions_guidees?.find((q) => q.id === questionId);

  if (!question) {
    return refus(
      'question_inconnue',
      `Le chapitre « ${chapitre.id} » n'a pas de question guidée « ${questionId} ».`,
    );
  }
  if (!question.choix.some((choix) => choix.id === choiceId)) {
    return refus(
      'choix_de_question_inconnu',
      `La question « ${questionId} » n'offre pas la réponse « ${choiceId} ».`,
    );
  }

  return {
    ok: true,
    state: withAction(
      { ...state, guidedAnswers: { ...state.guidedAnswers, [questionId]: choiceId } },
      { type: 'repondre_question_guidee', questionId, choiceId },
    ),
  };
}

/** La réponse donnée est-elle la bonne ? */
export function isGuidedAnswerCorrect(
  state: GameState,
  content: Content,
  questionId: string,
): boolean {
  const question = currentChapter(state, content).questions_guidees?.find(
    (q) => q.id === questionId,
  );
  const choisi = state.guidedAnswers[questionId];
  return Boolean(question?.choix.find((choix) => choix.id === choisi)?.correct);
}

/* ------------------------------------------------------------------ */
/* Étape 4 — Améliorer : revenir sur sa conception                     */
/* ------------------------------------------------------------------ */

/**
 * Remet la ville dans l'état d'avant le déploiement, pour que l'îlot puisse
 * repenser sa chaîne. Une seule fois.
 *
 * Les événements déjà survenus sont remis en jeu : la ville ayant changé, un
 * orage sur des lampadaires supervisés ne raconte plus la même histoire. C'est
 * précisément ce qu'on veut faire découvrir.
 *
 * La chaîne, elle, est conservée : on repart de ce qu'on avait construit.
 */
export function returnToDesign(state: GameState, content: Content): ActionResult {
  if (state.deployedSolutionId === null || state.gaugesBeforeDeploy === null) {
    return refus(
      'rien_a_redessiner',
      "Aucune solution n'est déployée : il n'y a rien à reprendre.",
    );
  }
  const retoursAutorises = content.reglages.retours_a_la_conception;
  if (state.redesignsUsed >= retoursAutorises) {
    return refus(
      'plus_de_retour_possible',
      `L'îlot a déjà utilisé ses retours à la conception (${retoursAutorises} au maximum).`,
    );
  }

  return {
    ok: true,
    state: withAction(
      {
        ...state,
        gauges: state.gaugesBeforeDeploy,
        gaugesBeforeDeploy: null,
        deployedSolutionId: null,
        triggeredEvents: [],
        eventChoices: {},
        redesignsUsed: state.redesignsUsed + 1,
      },
      { type: 'revenir_a_concevoir' },
    ),
  };
}

/** L'îlot peut-il encore revenir à l'étape Concevoir ? */
export function canReturnToDesign(state: GameState, content: Content): boolean {
  return (
    state.deployedSolutionId !== null &&
    state.redesignsUsed < content.reglages.retours_a_la_conception
  );
}

/**
 * Les quartiers que l'îlot a déjà équipés.
 *
 * Un quartier est équipé quand le problème qu'une solution déployée résout s'y
 * trouve. C'est ce qui allume ses lampadaires sur la carte — la récompense
 * visible du travail fourni.
 */
export function equippedDistricts(state: GameState, content: Content): readonly string[] {
  if (state.deployedSolutionId === null) {
    return [];
  }

  const solution = content.solutions.find((s) => s.id === state.deployedSolutionId);
  const probleme = content.problemes.find((p) => p.id === solution?.probleme);

  return probleme ? [probleme.quartier] : [];
}
